import React, { ChangeEvent, useState } from 'react';
import { TextField, SearchSelectItem } from '@td-client-frontend/common';
import ActivitySearchSelect from './ActivitySearchSelect';
import { ActivityDateRange } from './ActivityDateRange';
import { Button } from '../../Button';
import { useGetActivityTypes, useGetActivitySearch } from '../../../hooks/useActivities';
import { useDebounce } from 'use-debounce/lib';
import {
  ActivitySearchChangeObject,
  PartialActivityLogSearchQuery,
  ActivityDateRangeObject
} from '../../../@types/Audit';
import { useHistory } from 'react-router-dom';
import { format, subDays } from 'date-fns';

export interface GroupItem {
  label: string;
  value: string | number;
}

interface SearchFilterProps {
  onSearch: (values: PartialActivityLogSearchQuery) => void;
  onSearchReset: () => void;
  initialData: PartialActivityLogSearchQuery;
}

const MAX_VISIBLE_OPTIONS: number = 8;

const getPrefilledSearchOption = (
  data: PartialActivityLogSearchQuery,
  field: keyof PartialActivityLogSearchQuery
): SearchSelectItem[] => {
  if (field in data) {
    const stringValue = (data[field] as string).toLowerCase();
    return [{ label: stringValue, value: stringValue }];
  }
  return [];
};
export const DEFAULT_ITEM_PER_PAGE: { label: string; value: number } = { label: '10', value: 10 };
export const DEFAULT_PAGE_NUMBER: number = 1;
export const CURRENT_DATE: Date = new Date();

export const DEFAULT_GROUP_ACTIVITY_QUERY: PartialActivityLogSearchQuery = {
  itemPerPage: DEFAULT_ITEM_PER_PAGE.value,
  page: DEFAULT_PAGE_NUMBER,
  from: format(subDays(CURRENT_DATE, 30), 'MM/dd/yyyy'),
  to: format(CURRENT_DATE, 'MM/dd/yyyy')
};

const INITIAL_DATE_RANGE_DATA: ActivityDateRangeObject = {
  from: new Date(DEFAULT_GROUP_ACTIVITY_QUERY.from as string),
  to: new Date(DEFAULT_GROUP_ACTIVITY_QUERY.to as string)
};

export default function SearchFilters({ onSearch, onSearchReset, initialData }: SearchFilterProps) {
  const history = useHistory();

  const [searchQuery, setSearchQuery] = useState<PartialActivityLogSearchQuery>(initialData);
  const [employerSearchValue, setEmployerSearchValue] = useState<string>(
    initialData?.employerName?.toLowerCase() || ''
  );
  const [searchGroupNameValue, setSearchGroupNameValue] = useState<string>('');
  const [searchEmployerActivity, setSearchEmployerActivity] = useState<string>('');
  const [searchEmployerActivityType, setSearchEmployerActivityType] = useState<string>('');
  const [dateRangeData, setDateRangeData] = useState<ActivityDateRangeObject>(INITIAL_DATE_RANGE_DATA);

  const [debouncedEmployerSearch] = useDebounce<string>(
    employerSearchValue?.length >= 3 ? employerSearchValue : '',
    500
  );
  const [debouncedGroupNameSearch] = useDebounce<string>(
    searchGroupNameValue?.length >= 3 ? searchGroupNameValue : '',
    500
  );
  const { data: groupNameSearchOption } = useGetActivitySearch('groupName', debouncedGroupNameSearch);

  const { data: employerSearchOption } = useGetActivitySearch(
    'employerName',
    debouncedEmployerSearch,
    getPrefilledSearchOption(initialData, 'employerName')
  );

  const activityTypes = useGetActivityTypes(searchQuery.type ? searchQuery.type : '', 'group');

  const resetSearchState = () => {
    setEmployerSearchValue('');
    setSearchGroupNameValue('');
    setSearchEmployerActivity('');
    setSearchEmployerActivityType('');
    setSearchQuery(initialData);
    setDateRangeData(INITIAL_DATE_RANGE_DATA);
    history.push(history.location.pathname);
    onSearchReset();
  };

  const handleSearch = () => {
    const notEmptyQuery: PartialActivityLogSearchQuery = Object.fromEntries(
      Object.entries(searchQuery).filter(([_, v]) => v)
    );
    onSearch(notEmptyQuery);
  };

  const onSearchQueryChange = ({ key, value }: ActivitySearchChangeObject) => {
    setSearchQuery((prev: PartialActivityLogSearchQuery) => ({
      ...prev,
      [key]: value
    }));
  };

  const onUserNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    onSearchQueryChange({ key: 'user', value: event.target.value });
  };

  return (
    <div data-testid="searchFiltersForm" className="flex flex-col items-start">
      <div className="flex space-x-5">
        <ActivitySearchSelect
          onSearchChange={setEmployerSearchValue}
          setSelected={onSearchQueryChange}
          value={employerSearchValue}
          options={employerSearchOption}
          name="employerName"
          label="Employer"
          placeholder="employer search"
          clearOnClose={false}
        />
        <ActivitySearchSelect
          onSearchChange={setSearchGroupNameValue}
          setSelected={onSearchQueryChange}
          value={searchGroupNameValue}
          options={groupNameSearchOption}
          name="groupName"
          label="Group name"
          placeholder="group search"
          clearOnClose={false}
        />
        <TextField
          name="user"
          onChange={onUserNameChange}
          value={searchQuery.user}
          aria-label="User email"
          placeholder="username search"
          wrapperClassName="w-64"
          label="Username"
        />
      </div>
      <div className="flex space-x-4">
        <ActivityDateRange dateRangeData={dateRangeData} onChange={setDateRangeData} onSelect={onSearchQueryChange} />
        <ActivitySearchSelect
          onSearchChange={setSearchEmployerActivity}
          setSelected={onSearchQueryChange}
          value={searchEmployerActivity}
          options={activityTypes?.types}
          fieldWrapperWidthClass="w-64"
          placeholder="select employer activity"
          name="type"
          label="Employer activity"
        />
        <ActivitySearchSelect
          onSearchChange={setSearchEmployerActivityType}
          setSelected={onSearchQueryChange}
          value={searchEmployerActivityType}
          options={activityTypes?.tags}
          maxVisible={MAX_VISIBLE_OPTIONS}
          fieldWrapperWidthClass="w-64"
          placeholder="select action type"
          name="meta"
          label="Action type"
        />
        <div className="flex items-start space-x-4 mb-1 mt-7">
          <Button onClick={handleSearch}>Search</Button>
          <Button kind="default" className="whitespace-nowrap text-primary-500" onClick={resetSearchState}>
            Clear filters
          </Button>
        </div>
      </div>
    </div>
  );
}
